import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import StickyPageHeader from '../components/common/StickyPageHeader';
import {
  Plus,
  Edit2,
  Trash2,
  User,
  Mail,
  Phone,
  Search,
  History,
  ArrowDownLeft,
  ArrowUpRight,
  X,
  ChevronRight
} from 'lucide-react';
import { useToast } from '../context/ToastContext';
import { useConfig } from '../context/ConfigContext';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import {
  useContactsQuery,
  useDeleteContact,
  useContactHistoryQuery
} from '../hooks/useQueries';
import ConfirmModal from '../components/common/ConfirmModal';
import { ContactsSkeleton } from '../components/common/Skeleton';
import useModalBack from '../hooks/useModalBack';

const AVATAR_COLORS = [
  'bg-violet-500', 'bg-pink-500', 'bg-amber-500', 'bg-emerald-500',
  'bg-blue-500', 'bg-rose-500', 'bg-teal-500', 'bg-orange-500'
];

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const getAvatarColor = (name) => {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

export default function ContactsPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { formatAmount, formatDate, config } = useConfig();
  const { hasEncryptionKey } = useAuth();
  const { isModernUI } = useTheme();

  const [search, setSearch] = useState('');
  const [viewingHistory, setViewingHistory] = useState(null);
  const historyModalOpenRef = useRef(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, contact: null });

  useModalBack(!!viewingHistory, () => setViewingHistory(null), 'ContactHistory');

  // Keep ref in sync
  useEffect(() => {
    historyModalOpenRef.current = !!viewingHistory;
  }, [viewingHistory]);

  // Close history modal on browser back
  useEffect(() => {
    const handlePopState = () => {
      if (historyModalOpenRef.current) {
        setViewingHistory(null);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // React Query hooks
  const { data: contactsData = {}, isLoading } = useContactsQuery(
    { search: search || undefined },
    { enabled: hasEncryptionKey }
  );
  const deleteMutation = useDeleteContact();

  // Get history for the selected contact
  const { data: historyData = {}, isLoading: isHistoryLoading } = useContactHistoryQuery(
    viewingHistory?._id,
    { enabled: !!viewingHistory }
  );

  const contacts = contactsData.contacts || [];

  const handleAddNew = () => {
    navigate('/contacts/new');
  };

  const handleEdit = (contact) => {
    navigate(`/contacts/${contact._id}/edit`);
  };

  const handleViewHistory = useCallback((contact) => {
    setViewingHistory(contact);
    window.history.pushState({ historyModal: true }, '');
  }, []);

  const closeHistoryModal = useCallback(() => {
    if (historyModalOpenRef.current) {
      setViewingHistory(null);
      window.history.back();
    }
  }, []);

  const handleDeleteClick = (contact) => {
    setDeleteConfirm({ open: true, contact });
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirm.contact) {
      try {
        await deleteMutation.mutateAsync(deleteConfirm.contact._id);
        toast.success('Contact deleted');
      } catch (error) {
        toast.error(error.error || 'Failed to delete contact');
      }
    }
    setDeleteConfirm({ open: false, contact: null });
  };

  // Show skeleton on initial load
  if (isLoading && contacts.length === 0 && !search) {
    return <ContactsSkeleton />;
  }

  return (
    <div className="space-y-3 md:space-y-4">
      {/* Mobile Header */}
      <StickyPageHeader className="xl:hidden mb-2">
        <h1 className="text-base font-bold text-surface-900">Contacts</h1>
        <p className="text-[10px] text-surface-500">Manage your contacts for receivables & debts</p>
      </StickyPageHeader>

      {/* Desktop Header */}
      <div className="hidden xl:flex xl:items-center xl:justify-between gap-4">
        <div className="page-header mb-0">
          <h1 className="page-title">Contacts</h1>
          <p className="page-subtitle">
            Manage contacts for your receivables and debts
          </p>
        </div>
        <button
          onClick={handleAddNew}
          className="inline-flex items-center justify-center px-5 py-3 rounded-xl font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all"
          style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Contact
        </button>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm">🔍</span>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search contacts..."
          className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-surface-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none text-sm"
        />
      </div>

      {/* Empty State */}
      {!isLoading && contacts.length === 0 ? (
        <div className="card text-center py-12">
          <div className="w-16 h-16 bg-surface-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <User className="w-8 h-8 text-surface-400" />
          </div>
          <h3 className="text-lg font-medium text-surface-900 mb-2">
            {search ? 'No contacts found' : 'No contacts yet'}
          </h3>
          <p className="text-surface-500 mb-4">
            {search
              ? 'Try a different search term'
              : 'Add contacts to track receivables and debts'
            }
          </p>
          {!search && (
            <button
              onClick={handleAddNew}
              className="btn-primary inline-flex"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Contact
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="hidden xl:block card overflow-hidden p-0">
            <table className="w-full">
              <thead>
                <tr className="table-header border-b border-surface-200">
                  <th className="table-cell">Name</th>
                  <th className="table-cell">Email</th>
                  <th className="table-cell">Mobile</th>
                  <th className="table-cell w-32 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-100">
                {contacts.map((contact) => (
                  <tr
                    key={contact._id}
                    className="hover:bg-surface-50 transition-colors"
                  >
                    <td className="table-cell">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${getAvatarColor(contact.name)}`}>
                          {getInitials(contact.name)}
                        </div>
                        <span className="font-medium text-surface-900">
                          {contact.name}
                        </span>
                      </div>
                    </td>
                    <td className="table-cell">
                      {contact.email ? (
                        <div className="flex items-center gap-2 text-surface-600">
                          <Mail className="w-4 h-4 text-surface-400" />
                          {contact.email}
                        </div>
                      ) : (
                        <span className="text-surface-400">-</span>
                      )}
                    </td>
                    <td className="table-cell">
                      {contact.mobile ? (
                        <div className="flex items-center gap-2 text-surface-600">
                          <Phone className="w-4 h-4 text-surface-400" />
                          {contact.mobile}
                        </div>
                      ) : (
                        <span className="text-surface-400">-</span>
                      )}
                    </td>
                    <td className="table-cell text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleViewHistory(contact)}
                          className="p-2 text-blue-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="View History"
                        >
                          <History className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(contact)}
                          className="p-2 text-primary-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(contact)}
                          className="p-2 text-expense-500 hover:text-expense-600 hover:bg-expense-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards View */}
          <div className="xl:hidden space-y-2 pb-20">
            {contacts.map((contact) => {
              const initials = getInitials(contact.name);
              const avatarColor = getAvatarColor(contact.name);
              const receivable = contact.receivableTotal || 0;
              const debt = contact.debtTotal || 0;
              const net = receivable - debt;

              return (
                <div
                  key={contact._id}
                  className="bg-white rounded-2xl border border-surface-100 active:scale-[0.98] transition-all cursor-pointer overflow-hidden"
                  onClick={() => handleViewHistory(contact)}
                >
                  <div className="p-3.5">
                    <div className="flex items-center gap-3">
                      <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0 ${avatarColor}`}>
                        {initials}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-bold text-surface-900 text-[15px] truncate">{contact.name}</p>
                          {contact.notes && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-surface-100 text-surface-500 flex-shrink-0">{contact.notes}</span>
                          )}
                        </div>
                        {contact.mobile && (
                          <p className="text-xs text-surface-400 mt-0.5 flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {contact.mobile}
                          </p>
                        )}
                      </div>

                      {/* Net balance */}
                      {(receivable > 0 || debt > 0) ? (
                        <div className="text-right flex-shrink-0">
                          <p className="text-sm font-extrabold" style={{ color: net >= 0 ? config.incomeColor : config.expenseColor }}>
                            {net >= 0 ? '+' : ''}{formatAmount(Math.abs(net))}
                          </p>
                          <p className="text-[10px] text-surface-400">Net</p>
                        </div>
                      ) : (
                        <ChevronRight className="w-4 h-4 text-surface-300 flex-shrink-0" />
                      )}
                    </div>

                    {/* Balance row */}
                    {(receivable > 0 || debt > 0) && (
                      <div className="flex items-center gap-3 mt-2 ml-14">
                        {receivable > 0 && (
                          <span className="flex items-center gap-1 text-[11px]">
                            <ArrowDownLeft className="w-3 h-3" style={{ color: config.incomeColor }} />
                            <span style={{ color: config.incomeColor }} className="font-semibold">{formatAmount(receivable)}</span>
                          </span>
                        )}
                        {debt > 0 && (
                          <span className="flex items-center gap-1 text-[11px]">
                            <ArrowUpRight className="w-3 h-3" style={{ color: config.expenseColor }} />
                            <span style={{ color: config.expenseColor }} className="font-semibold">{formatAmount(debt)}</span>
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* FAB for Mobile */}
      <button
        onClick={handleAddNew}
        className="xl:hidden fab"
        style={{ opacity: 0.85 }}
        aria-label="Add contact"
      >
        <Plus className="w-6 h-6" />
      </button>

      {/* History Bottom Sheet (Mobile) / Centered Modal (Desktop) */}
      {viewingHistory && (
        <>
          {/* Overlay */}
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[100]" onClick={closeHistoryModal} />

          {/* Desktop: Centered Modal */}
          <div className="hidden xl:flex fixed inset-0 items-center justify-center z-[101] p-4">
            <div className="bg-white rounded-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
              <div className="p-4 border-b border-surface-100 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${getAvatarColor(viewingHistory.name)}`}>
                    {getInitials(viewingHistory.name)}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-surface-900">{viewingHistory.name}</h3>
                    <p className="text-xs text-surface-500">Receivables & debts history</p>
                  </div>
                </div>
                <button onClick={closeHistoryModal} className="p-2 hover:bg-surface-100 rounded-lg">
                  <X className="w-5 h-5 text-surface-400" />
                </button>
              </div>
              {renderHistoryContent()}
            </div>
          </div>

          {/* Mobile: Bottom Sheet */}
          <div className="xl:hidden fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl z-[101] animate-slide-up" style={{ maxHeight: '90vh' }}>
            {/* Drag Handle */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-9 h-1 bg-surface-300 rounded-full" />
            </div>

            {/* Header */}
            <div className="px-5 pb-3 flex items-center gap-3">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-base ${getAvatarColor(viewingHistory.name)}`}>
                {getInitials(viewingHistory.name)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-lg font-bold text-surface-900 truncate">{viewingHistory.name}</p>
                <div className="flex items-center gap-2 text-xs text-surface-400">
                  {viewingHistory.mobile && (
                    <span className="flex items-center gap-1">
                      <Phone className="w-3 h-3" />
                      {viewingHistory.mobile}
                    </span>
                  )}
                  {viewingHistory.email && (
                    <span className="flex items-center gap-1 truncate">
                      <Mail className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">{viewingHistory.email}</span>
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={closeHistoryModal}
                className="w-9 h-9 rounded-xl bg-surface-100 flex items-center justify-center flex-shrink-0"
              >
                <X className="w-4 h-4 text-surface-500" />
              </button>
            </div>

            {renderHistoryContent()}
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, contact: null })}
        onConfirm={handleDeleteConfirm}
        title="Delete Contact"
        message="Are you sure you want to delete this contact? This will not affect existing receivables or debts."
        confirmText="Delete"
        variant="danger"
      />
    </div>
  );

  function renderHistoryContent() {
    return (
      <div className="flex-1 overflow-y-auto px-5 pb-6" style={{ maxHeight: 'calc(90vh - 120px)' }}>
        {isHistoryLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 gap-2.5 mb-5 mt-2">
              <div className="rounded-xl p-3" style={{ backgroundColor: 'color-mix(in srgb, var(--accent-bg) 10%, white)' }}>
                <div className="flex items-center gap-1.5 mb-1">
                  <ArrowDownLeft className="w-3.5 h-3.5" style={{ color: config.incomeColor }} />
                  <span className="text-[11px] font-semibold" style={{ color: config.incomeColor }}>To Receive</span>
                </div>
                <p className="text-xl font-extrabold" style={{ color: config.incomeColor }}>
                  {formatAmount(historyData.summary?.totalReceivables || 0)}
                </p>
              </div>
              <div className="bg-red-50 rounded-xl p-3">
                <div className="flex items-center gap-1.5 mb-1">
                  <ArrowUpRight className="w-3.5 h-3.5" style={{ color: config.expenseColor }} />
                  <span className="text-[11px] font-semibold" style={{ color: config.expenseColor }}>To Pay</span>
                </div>
                <p className="text-xl font-extrabold" style={{ color: config.expenseColor }}>
                  {formatAmount(historyData.summary?.totalDebts || 0)}
                </p>
              </div>
            </div>

            {/* Receivables */}
            {historyData.receivables?.length > 0 && (
              <div className="mb-4">
                <h4 className="text-xs font-bold text-surface-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <ArrowDownLeft className="w-3.5 h-3.5" style={{ color: config.incomeColor }} />
                  Receivables ({historyData.receivables.length})
                </h4>
                <div className="space-y-2">
                  {historyData.receivables.map((r) => {
                    const paidPercent = r.amount > 0 ? Math.round(((r.paidAmount || 0) / r.amount) * 100) : 0;
                    return (
                      <div
                        key={r._id}
                        className="bg-surface-50 rounded-xl p-3 cursor-pointer active:scale-[0.98] transition-all"
                        onClick={() => { closeHistoryModal(); setTimeout(() => navigate(`/receivables/${r._id}`), 100); }}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm font-semibold text-surface-900 line-clamp-2 flex-1">{r.description}</p>
                          <p className="text-sm font-extrabold flex-shrink-0" style={{ color: config.incomeColor }}>
                            {formatAmount(r.amount || 0)}
                          </p>
                        </div>
                        <div className="flex items-center justify-between mt-1.5">
                          <span className="text-[11px] text-surface-400">{formatDate(r.lentDate)}</span>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                            r.status === 'settled' ? 'bg-green-100 text-green-700' :
                            r.status === 'partial' ? 'bg-blue-100 text-blue-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {r.status}
                          </span>
                        </div>
                        {r.status === 'partial' && r.amount > 0 && (
                          <div className="mt-2">
                            <div className="h-1.5 bg-surface-200 rounded-full overflow-hidden">
                              <div className="h-full rounded-full bg-green-500 transition-all" style={{ width: `${paidPercent}%` }} />
                            </div>
                            <p className="text-[10px] text-surface-400 mt-0.5 text-right">{paidPercent}% received</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Debts */}
            {historyData.debts?.length > 0 && (
              <div className="mb-4">
                <h4 className="text-xs font-bold text-surface-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <ArrowUpRight className="w-3.5 h-3.5" style={{ color: config.expenseColor }} />
                  Debts ({historyData.debts.length})
                </h4>
                <div className="space-y-2">
                  {historyData.debts.map((d) => {
                    const paidPercent = d.amount > 0 ? Math.round(((d.paidAmount || 0) / d.amount) * 100) : 0;
                    return (
                      <div
                        key={d._id}
                        className="bg-surface-50 rounded-xl p-3 cursor-pointer active:scale-[0.98] transition-all"
                        onClick={() => { closeHistoryModal(); setTimeout(() => navigate(`/debts/${d._id}`), 100); }}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm font-semibold text-surface-900 line-clamp-2 flex-1">{d.description}</p>
                          <p className="text-sm font-extrabold flex-shrink-0" style={{ color: config.expenseColor }}>
                            {formatAmount(d.amount || 0)}
                          </p>
                        </div>
                        <div className="flex items-center justify-between mt-1.5">
                          <span className="text-[11px] text-surface-400">{formatDate(d.borrowedDate)}</span>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                            d.status === 'settled' ? 'bg-green-100 text-green-700' :
                            d.status === 'partial' ? 'bg-blue-100 text-blue-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {d.status}
                          </span>
                        </div>
                        {d.status === 'partial' && d.amount > 0 && (
                          <div className="mt-2">
                            <div className="h-1.5 bg-surface-200 rounded-full overflow-hidden">
                              <div className="h-full rounded-full bg-green-500 transition-all" style={{ width: `${paidPercent}%` }} />
                            </div>
                            <p className="text-[10px] text-surface-400 mt-0.5 text-right">{paidPercent}% paid</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="xl:hidden grid grid-cols-2 gap-2 mt-4 pb-2">
              <button
                onClick={(e) => { e.stopPropagation(); closeHistoryModal(); setTimeout(() => handleEdit(viewingHistory), 100); }}
                className="flex items-center justify-center gap-2 py-3 rounded-xl border border-surface-200 text-sm font-semibold text-surface-700"
              >
                <Edit2 className="w-4 h-4" />
                Edit
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); closeHistoryModal(); setTimeout(() => handleDeleteClick(viewingHistory), 100); }}
                className="flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold text-red-500 bg-red-50"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </div>

            {/* Empty State */}
            {(!historyData.receivables?.length && !historyData.debts?.length) && (
              <div className="text-center py-8">
                <History className="w-12 h-12 text-surface-300 mx-auto mb-2" />
                <p className="text-sm text-surface-500">No receivables or debts with this contact</p>
              </div>
            )}
          </>
        )}
      </div>
    );
  }
}
